import { Loader } from '@googlemaps/js-api-loader';

let googleMapsPromise: Promise<typeof google> | null = null;

const styles = [
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#e9e9e9' }, { lightness: 17 }],
  },
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [{ color: '#f5f5f5' }, { lightness: 20 }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.fill',
    stylers: [{ color: '#ffffff' }, { lightness: 17 }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#ffffff' }, { lightness: 29 }, { weight: 0.2 }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }, { lightness: 18 }],
  },
  {
    featureType: 'road.local',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }, { lightness: 16 }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#f5f5f5' }, { lightness: 21 }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#dedede' }, { lightness: 21 }],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [{ visibility: 'on' }, { color: '#ffffff' }, { lightness: 16 }],
  },
  {
    elementType: 'labels.text.fill',
    stylers: [{ saturation: 36 }, { color: '#333333' }, { lightness: 40 }],
  },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#f2f2f2' }, { lightness: 19 }],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.fill',
    stylers: [{ color: '#fefefe' }, { lightness: 20 }],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#fefefe' }, { lightness: 17 }, { weight: 1.2 }],
  },
];

function loadGoogleMaps() {
  if (!googleMapsPromise) {
    const loader = new Loader({
      apiKey: 'AIzaSyACeL0ltu60rAtGGegL7p0iaUki_RKM30k',
      version: 'weekly',
    });

    googleMapsPromise = loader.load();
  }

  return googleMapsPromise;
}

export async function initSimpleMap() {
  const wrapper = document.querySelector<HTMLElement>('.js-rlx-simple-map');
  if (!wrapper) {
    return;
  }

  const mapEl = wrapper.querySelector('.js-rlx-map') as HTMLElement;

  const lat = parseFloat(mapEl.dataset.lat || '');
  const lng = parseFloat(mapEl.dataset.lng || '');
  if (isNaN(lat) || isNaN(lng)) {
    return;
  }

  const google = await loadGoogleMaps();

  const map = new google.maps.Map(mapEl, {
    center: { lat, lng },
    zoom: 15,
    styles: styles,
    mapTypeId: 'roadmap',
    disableDefaultUI: true,
    zoomControl: false,
  });

  new google.maps.Marker({
    position: { lat, lng },
    map,
    icon: {
      url: '/img/rolex-v7/map-location-pin-green-icon.svg',
      scaledSize: new google.maps.Size(50, 50),
    },
  });
}

export async function initComplexMap() {
  const wrapper = document.querySelector<HTMLElement>('.js-rlx-dynamic-map');
  if (!wrapper) {
    return;
  }

  const mapEl = wrapper.querySelector('.js-rlx-map') as HTMLElement;
  const locationEls = wrapper.querySelectorAll<HTMLElement>('.js-rlx-location');
  if (!mapEl || locationEls.length === 0) {
    return;
  }

  const { default: defaultZoom, active: activeZoom } = getResponsiveZoom();
  const center = { lat: 44.549523, lng: -76.724228 };
  const google = await loadGoogleMaps();

  const defaultIcon = {
    url: '/img/rolex-v7/map-location-pin-green-icon.svg',
    scaledSize: new google.maps.Size(50, 50),
  };

  const activeIcon = {
    url: '/img/rolex-v7/map-location-pin-selected-green-icon.svg',
    scaledSize: new google.maps.Size(50, 50),
  };

  const map = new google.maps.Map(mapEl, {
    center,
    zoom: defaultZoom,
    styles,
    mapTypeId: 'roadmap',
    disableDefaultUI: true,
    zoomControl: false,
  });

  const locations = Array.from(locationEls)
    .map((location) => ({
      lat: parseFloat(location.dataset.lat || ''),
      lng: parseFloat(location.dataset.lng || ''),
      title: location.dataset.title || '',
      region: location.dataset.region || '',
      element: location,
    }))
    .filter((loc) => !isNaN(loc.lat) && !isNaN(loc.lng));

  let activeMarker: google.maps.Marker | null = null;
  const listCards = wrapper.querySelectorAll<HTMLElement>('.rlx-dynamic-map__card');

  // Markers indexed by region so the region filter can show/hide + recentre them
  const regionMarkers: {
    marker: google.maps.Marker;
    region: string;
    position: { lat: number; lng: number };
  }[] = [];

  locations.forEach((location, i) => {
    const marker = new google.maps.Marker({
      position: { lat: location.lat, lng: location.lng },
      map,
      title: location.title,
      icon: defaultIcon,
    });

    regionMarkers.push({
      marker,
      region: location.region,
      position: { lat: location.lat, lng: location.lng },
    });

    const openLocation = () => {
      // On mobile (map view): zoom in to pin; on desktop: keep both pins visible
      if (window.matchMedia('(max-width: 767px)').matches) {
        map.setZoom(activeZoom);
        map.panTo({ lat: location.lat, lng: location.lng });
        const offset = getMapOffset();
        map.panBy(offset.x, offset.y);
      }

      wrapper
        .querySelectorAll<HTMLElement>('.js-rlx-location')
        .forEach((el) => hideLocation(el));
      showLocation(location.element);

      if (activeMarker) {
        activeMarker.setIcon(defaultIcon);
      }
      marker.setIcon(activeIcon);
      activeMarker = marker;

      listCards.forEach((c) => c.classList.remove('is-active'));
      listCards[i]?.classList.add('is-active');
      wrapper.classList.add('has-active-location');

      // Redraw map after layout grows to fit content; re-centre to keep both pins visible
      setTimeout(() => {
        google.maps.event.trigger(map, 'resize');
        map.setCenter(center);
      }, 50);
    };

    marker.addListener('click', openLocation);

    const closeBtn = location.element.querySelector('.js-rlx-close');
    closeBtn?.addEventListener('click', () => {
      hideLocation(location.element);
      map.panTo(center);
      map.setZoom(defaultZoom);

      if (activeMarker) {
        activeMarker.setIcon(defaultIcon);
      }
      activeMarker = null;
      listCards.forEach((c) => c.classList.remove('is-active'));
      wrapper.classList.remove('has-active-location');
    });
  });

  const zoomInBtn = wrapper.querySelector<HTMLButtonElement>('.js-rlx-zoom-in');
  const zoomOutBtn =
    wrapper.querySelector<HTMLButtonElement>('.js-rlx-zoom-out');

  zoomInBtn?.addEventListener('click', () => {
    const currentZoom = map.getZoom() || 0;
    map.setZoom(currentZoom + 1);
  });

  zoomOutBtn?.addEventListener('click', () => {
    const currentZoom = map.getZoom() || 0;
    map.setZoom(currentZoom - 1);
  });

  // Filter buttons (Rolex Retailer / Service Centre)
  const filterBtns = wrapper.querySelectorAll<HTMLButtonElement>('.js-rlx-filter');
  const cards = wrapper.querySelectorAll<HTMLElement>('.rlx-dynamic-map__card');

  filterBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      filterBtns.forEach((b) => b.classList.remove('is-active'));
      btn.classList.add('is-active');

      const filter = btn.dataset.filter;
      cards.forEach((card) => {
        const types = (card.dataset.types || '').split(' ');
        card.style.display = filter === 'all' || types.includes(filter!) ? '' : 'none';
      });
    });
  });

  // Mobile "View map ▼" / "View list ▼" toggle
  const viewBtns = wrapper.querySelectorAll<HTMLButtonElement>('.js-rlx-view-btn');
  const listPanel = wrapper.querySelector<HTMLElement>('.js-rlx-list-panel');
  const mapWrapper = wrapper.querySelector<HTMLElement>('.js-rlx-map-wrapper');

  viewBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      if (btn.dataset.view === 'map') {
        listPanel?.classList.add('is-hidden');
        mapWrapper?.classList.add('is-open');
        wrapper.classList.add('is-map-view');
        // Force map to re-render after becoming visible
        google.maps.event.trigger(map, 'resize');
        map.setCenter(center);
      } else {
        mapWrapper?.classList.remove('is-open');
        listPanel?.classList.remove('is-hidden');
        wrapper.classList.remove('is-map-view');
      }
    });
  });

  // Region filter (Ontario / Québec) — top nav. Shows only the matching
  // location(s) in both the list and on the map, and recentres the map.
  const regionBtns = wrapper.querySelectorAll<HTMLButtonElement>('.js-rlx-region');

  const applyRegion = (region: string) => {
    regionBtns.forEach((b) => {
      const isActive = b.dataset.region === region;
      b.classList.toggle('is-active', isActive);
      b.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    // List cards: .is-region-hidden (display:none !important) composes with the
    // Retailer/Service filter's inline display.
    listCards.forEach((card) => {
      card.classList.toggle(
        'is-region-hidden',
        !!region && card.dataset.region !== region
      );
    });

    // Map markers: only show the active region's pins
    const visible = regionMarkers.filter((m) => !region || m.region === region);
    regionMarkers.forEach((m) =>
      m.marker.setMap(!region || m.region === region ? map : null)
    );

    // Recentre: on a single matching location, zoom into that city; otherwise
    // fall back to the both-pins overview.
    if (visible.length === 1) {
      map.panTo(visible[0].position);
      map.setZoom(window.matchMedia('(min-width: 768px)').matches ? 12 : 11);
    } else {
      map.panTo(center);
      map.setZoom(defaultZoom);
    }
    google.maps.event.trigger(map, 'resize');
  };

  regionBtns.forEach((btn) => {
    btn.addEventListener('click', () => applyRegion(btn.dataset.region || ''));
  });

  // Default to the first/active region so one is always selected (matches design)
  const initialRegion =
    wrapper.querySelector<HTMLButtonElement>('.js-rlx-region.is-active')?.dataset
      .region ||
    regionBtns[0]?.dataset.region ||
    '';
  if (initialRegion) {
    applyRegion(initialRegion);
  }
}

function hideLocation(el: HTMLElement) {
  el.setAttribute('aria-hidden', 'true');
  el.setAttribute('tabindex', '-1');
  el.querySelectorAll<HTMLElement>(
    'a, button, input, select, textarea'
  ).forEach((focusable) => focusable.setAttribute('tabindex', '-1'));
}

function showLocation(el: HTMLElement) {
  el.removeAttribute('aria-hidden');
  el.removeAttribute('tabindex');
  el.querySelectorAll<HTMLElement>(
    'a, button, input, select, textarea'
  ).forEach((focusable) => focusable.removeAttribute('tabindex'));

  const firstFocusable =
    el.querySelector<HTMLElement>('a, button, input, select, textarea');

  if (firstFocusable) {
    firstFocusable.focus();
  }
}

function getResponsiveZoom(): { default: number; active: number } {
  return window.matchMedia('(min-width: 768px)').matches
    ? { default: 7, active: 15 }
    : { default: 6, active: 12 };
}

function getMapOffset(): { x: number; y: number } {
  const isMobile = window.matchMedia('(max-width: 767px)').matches;

  if (isMobile) {
    const drawerHeight =
      document.querySelector('.js-rlx-location')?.clientHeight || 0;
    return { x: 0, y: drawerHeight / 2 };
  } else {
    const sidebarWidth =
      document.querySelector('.js-rlx-location')?.clientWidth || 0;
    return { x: -sidebarWidth / 2, y: 0 };
  }
}
